import useTranslate from "../../../core/languages/hooks/useTranslate";
import React, { useEffect, useRef, useState } from "react";
import ClearDataButton from "../../../common/components/filters/ClearDataButton";
import { BookingFilters } from "../../hooks/useBookingMeSearchData";

interface Props {
    query: { confirmed?: boolean; canceled?: boolean };
    onChange: (updates: Partial<BookingFilters>) => void;
}

const BookingStatusFilter: React.FC<Props> = ({ query, onChange }) => {
    const t = useTranslate();
    const statusRef = useRef<HTMLDivElement | null>(null);
    const [showStatusPopover, setShowStatusPopover] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<"confirmed" | "pending" | "canceled" | null>(null);

    const optionsStatus = [
        {label: t("bookingMeForm.confirmed"), value: "confirmed"},
        {label: t("bookingMeForm.pending"), value: "pending"},
        {label: t("bookingMeForm.canceled"), value: "canceled"},
    ];

    const handleSelectStatus = (value: "confirmed" | "pending" | "canceled" | null) => {
        setSelectedStatus(value);
        setShowStatusPopover(false);

        if (value === "confirmed") {
            onChange({ confirmed: true, canceled: false });
        } else if (value === "pending") {
            onChange({ confirmed: false, canceled: false });
        } else if (value === "canceled") {
            onChange({ canceled: true, confirmed: false });
        } else {
            onChange({ confirmed: undefined, canceled: undefined });
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (statusRef.current && !statusRef.current.contains(e.target as Node)) {
            setShowStatusPopover(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const renderPopoverOptions = (
        options: Array<{ value: string | null | undefined; label: React.ReactNode }>,
        onSelect: (value: any) => void
    ) =>
        options.map((opt) => (
            <div
                key={String(opt.value)}
                className="flex px-4 sm:px-8 items-center py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                onClick={() => onSelect(opt.value)}
            >
                <span className="block font-medium text-neutral-700 dark:text-neutral-200">{opt.label}</span>
            </div>
        ));

    return (
        <>
            {/* Status Selector */}
            <div
                ref={statusRef}
                className="relative w-60 cursor-pointer items-center min-w-0 [ nc-hero-field-padding ]"
            >
                <div className="flex-grow">
                    <div
                        className="w-full p-0 xl:text-lg font-semibold min-w-0 cursor-pointer text-neutral-800 dark:text-neutral-100"
                        onClick={() => setShowStatusPopover((v) => !v)}
                    >
                        {selectedStatus ? (
                            <span className="whitespace-normal break-words">
                                {optionsStatus.find((o) => o.value === selectedStatus)?.label}
                            </span>
                        ) : (
                            <span className="block whitespace-normal break-words text-neutral-800 dark:text-neutral-200">
                                {t("bookingMeForm.bookingStatus")}
                            </span>
                        )}
                    </div>
                    <span className="block mt-0.5 text-sm text-neutral-400 font-light">
                        {t("bookingMeForm.searchByBookingStatus")}
                    </span>
                </div>
                {selectedStatus && <ClearDataButton onClick={() => handleSelectStatus(null)} />}
                {showStatusPopover && (
                    <div className="absolute left-0 z-40 w-full bg-white dark:bg-neutral-800 top-full mt-3 py-3 rounded-3xl shadow-xl max-h-60 overflow-y-auto">
                        {renderPopoverOptions(optionsStatus, handleSelectStatus)}
                    </div>
                )}
            </div>
        </>
    );
};

export default BookingStatusFilter;
